import { useEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { useEscapeLayer } from '@/components/escapeLayer';
import { HaiIcon } from '@/components/icons';

/**
 * Kebab(⋯)選單(從 `ProjectListPage` 抽出的純結構重構,行為不變)
 * -----------------------------------------------------------------------------
 * ProjectCard / ProjectRow 共用:⋯ 按鈕 + [繼續編輯 / 刪除] dropdown。
 */

/**
 * 共用 kebab(⋯)選單 controller — ProjectCard / ProjectRow 共用。
 * 回傳 open state + ref(掛在外層 wrapper 偵測 click-outside 自動收合)。
 *
 * R393(CIS「下拉式選單」:點外面與 ESC 皆可關)— ESC 走 `escapeLayer`(只在展開時佔一層;
 * 疊在別的視窗上時一次只收這個選單),收起後焦點還給 ⋯ 鈕(鍵盤使用者不會掉到 body)。
 */
export function useKebabMenu() {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEscapeLayer(() => {
    setMenuOpen(false);
    menuRef.current?.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')?.focus();
  }, menuOpen);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return { menuOpen, setMenuOpen, menuRef };
}

/**
 * Kebab 選單(⋯ 按鈕 + [繼續編輯 / 刪除] dropdown);ProjectCard / ProjectRow 共用。
 * `align` 控制 dropdown 對齊方向(card 右上 → 'right';list row 也靠右)。
 */
export function KebabMenu({
  open,
  setOpen,
  menuRef,
  onOpen,
  onEdit,
  onDelete,
  deleting,
}: {
  open: boolean;
  setOpen: (v: boolean) => void;
  menuRef: RefObject<HTMLDivElement>;
  onOpen: () => void;
  /** R292(FB-20260828-0008)— 開「編輯專案資訊」dialog;缺省不渲染該項(相容既有呼叫端)。 */
  onEdit?: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t } = useTranslation('projects');
  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-label={t('list.card.moreActions')}
        title={t('list.card.moreActions')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        style={kebabBtnStyle}
      >
        <HaiIcon name="more" size={14} />
      </button>
      {open && (
        <div role="menu" style={menuStyle}>
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onOpen();
            }}
            style={menuItemStyle}
          >
            {t('list.card.openCanvas')}
          </button>
          {/* FB-20260828-0008 — 使用者點「⋯」是想改專案資料,以前只有「進畫布/刪除」兩項,
              而「繼續編輯」字面讀起來像能改專案 → 加真的編輯入口,原項改名「開啟畫布」。 */}
          {onEdit && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onEdit();
              }}
              style={menuItemStyle}
              data-testid="project-menu-edit"
            >
              {t('list.card.editInfo')}
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onDelete();
            }}
            disabled={deleting}
            // CIS 刪除 = 玫瑰;`--err` 是色相識別不是可讀前景(R266:當字只有 3.35:1)
            style={{ ...menuItemStyle, color: 'var(--rose-700)' }}
          >
            {t('list.card.delete')}
          </button>
        </div>
      )}
    </div>
  );
}

const kebabBtnStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  background: 'color-mix(in oklab, var(--ink-900) 95%, transparent)',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--ink-400)',
  backdropFilter: 'blur(6px)',
};

const menuStyle: CSSProperties = {
  position: 'absolute',
  top: 32,
  right: 0,
  background: 'var(--ink-900)',
  border: '1px solid var(--hairline-strong)',
  borderRadius: 'var(--r-sm)',
  boxShadow: 'var(--shadow-md)',
  minWidth: 120,
  padding: '4px 0',
  zIndex: 10,
};

const menuItemStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '8px 14px',
  background: 'transparent',
  border: 'none',
  fontSize: 13,
  color: 'var(--ink-300)',
  cursor: 'pointer',
};
